import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface FAQItemProps {
  question: string;
  answer: string;
  index: number;
  openIndex: number | null;
  onToggle: (index: number) => void;
  delay: number;
}

function FAQItem({ question, answer, index, openIndex, onToggle, delay }: FAQItemProps) {
  const { elementRef } = useScrollAnimation({ delay });
  const isOpen = openIndex === index;

  return (
    <div
      ref={elementRef}
      className="animate-fade-up rounded-xl overflow-hidden transition-all duration-300"
      style={{
        background: isOpen ? '#fffdf5' : '#fafafa',
        border: `2px solid ${isOpen ? '#B8860B' : '#ebebeb'}`,
      }}
    >
      <button
        onClick={() => onToggle(index)}
        className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer transition-all duration-200"
      >
        <h3
          className="text-base sm:text-lg font-bold pr-4"
          style={{ color: isOpen ? '#B8860B' : '#1a1a1a' }}
        >
          {question}
        </h3>
        <div
          className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300"
          style={{
            background: isOpen ? 'linear-gradient(90deg, #B8860B, #F5D76E)' : '#ebebeb',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <i className="ri-arrow-down-s-line text-xl" style={{ color: isOpen ? '#000000' : '#555555' }}></i>
        </div>
      </button>
      <div
        className="overflow-hidden transition-all duration-300"
        style={{
          maxHeight: isOpen ? '500px' : '0',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div className="px-6 pb-5">
          <p className="text-sm sm:text-base leading-relaxed text-gray-600">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const { elementRef: headerRef } = useScrollAnimation();
  const { elementRef: ctaRef } = useScrollAnimation();

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'What if I travel a lot?',
      answer: 'No problem. The program is designed for busy professionals. You\'ll get travel-friendly workout plans, hotel room routines, and flexible meal options. Plus, weekly check-ins keep you accountable no matter where you are in the world.',
    },
    {
      question: 'Do I need a gym?',
      answer: 'Not necessarily. While gym access is ideal, I can create effective home or hotel workout plans using minimal equipment. The key is consistency and proper programming, which I provide regardless of your training environment.',
    },
    {
      question: 'How is this different from other coaches?',
      answer: 'Most coaches give you a generic plan and disappear. I provide daily accountability, personalized adjustments based on your progress, mindset coaching, and direct access to me. This isn\'t just training, it\'s a complete transformation system backed by a 90-day guarantee.',
    },
    {
      question: 'What if I don\'t see results?',
      answer: 'If you follow the plan for 90 days and don\'t see measurable results, I\'ll refund every penny. No questions asked. But here\'s the truth: if you show up and do the work, you WILL see results. That\'s why I can make this guarantee.',
    },
    {
      question: 'How much time per week does this require?',
      answer: '4-6 hours per week for training, plus meal prep time. This is designed for busy men who don\'t have all day to spend in the gym. Every workout is efficient, focused, and gets you in and out fast while maximizing results.',
    },
    {
      question: 'Is there a contract?',
      answer: 'No long-term contracts. You can choose monthly or pay upfront for better value. However, real transformation takes time. I recommend committing to at least 90 days to see the full results and build lasting habits.',
    },
    {
      question: 'What happens after I apply?',
      answer: 'After you submit the booking form, I\'ll review your information and reach out within 24 hours to schedule a free consultation call. We\'ll discuss your goals, assess if we\'re a good fit, and if so, get you started immediately.',
    },
    {
      question: 'Who is this NOT for?',
      answer: 'This is NOT for people looking for quick fixes, magic pills, or shortcuts. It\'s NOT for those who make excuses or aren\'t willing to follow the plan. And it\'s NOT for people who aren\'t ready to invest in themselves. This is for serious men only.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 sm:py-24" style={{ background: '#ffffff' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className="animate-fade-up text-center mb-12 sm:mb-16">
          <p className="text-sm font-bold tracking-[0.3em] uppercase mb-3" style={{ color: '#B8860B' }}>
            Questions Answered
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="w-16 h-1 mx-auto rounded-full" style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)' }} />
          <p className="text-base sm:text-lg mt-6 text-gray-500">
            Everything you need to know before you commit
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              index={index}
              openIndex={openIndex}
              onToggle={toggleFAQ}
              delay={index * 50}
            />
          ))}
        </div>

        <div ref={ctaRef} className="animate-fade-up text-center mt-12">
          <p className="text-base mb-6 text-gray-500">
            Still have questions? Let's talk.
          </p>
          <a
            href="#booking"
            className="inline-block px-8 py-4 rounded-lg font-bold text-base transition-all duration-300 shadow-md whitespace-nowrap cursor-pointer"
            style={{
              background: 'linear-gradient(90deg, #B8860B, #F5D76E)',
              color: '#000000',
            }}
          >
            Book Your Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
